import { FileText, Heart, BellRing, CheckCircle2, ArrowRight, Clock, Calendar } from "lucide-react";
import { STATUS_LABEL, STATUS_STYLE } from "./ApplicationsList";
import { Badge } from "../ui/badge";

function timeAgo(iso) {
  if (!iso) return "";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (d <= 0) return "Hoje";
  if (d === 1) return "Ontem";
  if (d < 7) return `Há ${d} dias`;
  const w = Math.floor(d / 7);
  return w === 1 ? "Há 1 semana" : `Há ${w} semanas`;
}

export function ActivityFeed({ apps = [], saved = [], alerts = [], onOpenApp, onOpenTab }) {
  // Constrói uma lista unificada de "eventos" ordenados por data desc
  const events = [];

  for (const a of apps.slice(0, 8)) {
    events.push({
      id: `app-${a.id}`, ts: a.updated_at || a.created_at,
      kind: "application", data: a,
    });
  }
  for (const s of saved.slice(0, 5)) {
    events.push({
      id: `saved-${s.id}`, ts: s.created_at,
      kind: "saved", data: s,
    });
  }
  for (const a of alerts.slice(0, 3)) {
    events.push({
      id: `alert-${a.id}`, ts: a.created_at,
      kind: "alert", data: a,
    });
  }

  events.sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
  const top = events.slice(0, 8);

  if (top.length === 0) {
    return (
      <section data-testid="activity-feed" className="bg-white rounded-2xl border border-black/5 shadow-sm p-5">
        <h2 className="font-heading font-black tracking-tighter text-lg text-slate-900 mb-2">Atividade recente</h2>
        <p className="text-sm text-slate-500">Sem atividade ainda. Candidata-te a uma vaga para veres o feed aqui.</p>
      </section>
    );
  }

  return (
    <section data-testid="activity-feed" className="bg-white rounded-2xl border border-black/5 shadow-sm p-5 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-black tracking-tighter text-lg text-slate-900">Atividade recente</h2>
        <Clock className="w-4 h-4 text-slate-400" />
      </div>
      <ul className="space-y-3">
        {top.map((ev) => {
          if (ev.kind === "application") {
            const a = ev.data;
            return (
              <li key={ev.id}
                data-testid={`activity-app-${a.id}`}
                className="flex items-start gap-3 rounded-xl border border-black/5 p-3 hover:bg-slate-50 cursor-pointer transition"
                onClick={() => onOpenApp && onOpenApp(a)}>
                <div className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 flex items-center justify-center shrink-0">
                  <FileText className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">
                    Candidatura a <span className="font-semibold">{a.job_title}</span>
                  </p>
                  <p className="text-xs text-slate-500">{a.company_name} · {timeAgo(ev.ts)}</p>
                </div>
                <Badge className={`${STATUS_STYLE[a.status]} hover:${STATUS_STYLE[a.status]} shrink-0 text-[10px]`}>
                  {STATUS_LABEL[a.status]}
                </Badge>
              </li>
            );
          }
          if (ev.kind === "saved") {
            return (
              <li key={ev.id}
                className="flex items-start gap-3 rounded-xl border border-black/5 p-3 hover:bg-slate-50 cursor-pointer transition"
                onClick={() => onOpenTab && onOpenTab("saved")}>
                <div className="w-9 h-9 rounded-full bg-rose-50 text-rose-700 flex items-center justify-center shrink-0">
                  <Heart className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">Guardaste uma vaga nos teus favoritos</p>
                  <p className="text-xs text-slate-500">{timeAgo(ev.ts)}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-400" />
              </li>
            );
          }
          if (ev.kind === "alert") {
            const al = ev.data;
            return (
              <li key={ev.id}
                className="flex items-start gap-3 rounded-xl border border-black/5 p-3 hover:bg-slate-50 cursor-pointer transition"
                onClick={() => onOpenTab && onOpenTab("alerts")}>
                <div className="w-9 h-9 rounded-full bg-blue-50 text-blue-700 flex items-center justify-center shrink-0">
                  <BellRing className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-900">
                    Alerta ativo — <span className="font-semibold">{al.category || "todas as categorias"}</span>
                    {al.district ? ` em ${al.district}` : ""}
                  </p>
                  <p className="text-xs text-slate-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> Criado {timeAgo(ev.ts)}
                  </p>
                </div>
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </li>
            );
          }
          return null;
        })}
      </ul>
    </section>
  );
}
